import type {
  AreaSesmt,
  CategoriaDoWorkItem,
  ComplexidadeAcao,
  ModuloDoSistema,
  PrioridadeAcao,
  SaudePrazo,
  SituacaoAcao,
  TipoTransicao,
} from '@/types/api';

/**
 * Rotulos e tokens de cor das enumeracoes de acao.
 *
 * Um lugar so. Espalhar esses textos pelas telas produziria "Em analise" numa e
 * "Em Análise" noutra, e um `switch` sem caso novo quando a enumeracao crescer.
 *
 * Os Record sao completos por tipo: acrescentar um valor a enumeracao no backend
 * e refletir no tipo do cliente passa a QUEBRAR A COMPILACAO aqui, em vez de
 * produzir celula vazia na tela.
 */

export const rotuloDaSituacao: Record<SituacaoAcao, string> = {
  Rascunho: 'Rascunho',
  AguardandoAnalise: 'Aguardando análise',
  EmAnalise: 'Em análise',
  AjusteSolicitado: 'Ajuste solicitado',
  Rejeitada: 'Rejeitada',
  Aprovada: 'Aprovada',
  Backlog: 'Backlog',
  EmDesenvolvimento: 'Em desenvolvimento',
  Concluida: 'Concluída',
  Cancelada: 'Cancelada',
};

/** Variavel CSS de cada situacao. As 10 tem cor distinta nos dois temas. */
export const tokenDaSituacao: Record<SituacaoAcao, string> = {
  Rascunho: '--situacao-rascunho',
  AguardandoAnalise: '--situacao-aguardando-analise',
  EmAnalise: '--situacao-em-analise',
  AjusteSolicitado: '--situacao-ajuste-solicitado',
  Rejeitada: '--situacao-rejeitada',
  Aprovada: '--situacao-aprovada',
  Backlog: '--situacao-backlog',
  EmDesenvolvimento: '--situacao-em-desenvolvimento',
  Concluida: '--situacao-concluida',
  Cancelada: '--situacao-cancelada',
};

/**
 * Ordem de exibicao das situacoes, seguindo o fluxo real do trabalho.
 *
 * Nao e a ordem do enum nem alfabetica: o filtro fica mais facil de ler quando
 * acompanha o caminho que a acao percorre.
 */
export const situacoesEmOrdem: SituacaoAcao[] = [
  'Rascunho',
  'AguardandoAnalise',
  'EmAnalise',
  'AjusteSolicitado',
  'Aprovada',
  'Backlog',
  'EmDesenvolvimento',
  'Concluida',
  'Rejeitada',
  'Cancelada',
];

export const rotuloDaSaudeDoPrazo: Record<SaudePrazo, string> = {
  SemPrazo: 'Sem prazo',
  NoPrazo: 'No prazo',
  ProximoVencimento: 'Próximo do vencimento',
  Atrasado: 'Atrasada',
  NaoAplicavel: 'Encerrada',
};

export const tokenDoPrazo: Record<SaudePrazo, string> = {
  SemPrazo: '--prazo-sem-prazo',
  NoPrazo: '--prazo-no-prazo',
  ProximoVencimento: '--prazo-proximo-vencimento',
  Atrasado: '--prazo-atrasado',
  // Acao encerrada nao tem prazo relevante: tratada como ausencia, e nao como
  // estado proprio de cor.
  NaoAplicavel: '--prazo-sem-prazo',
};

export const rotuloDaPrioridade: Record<PrioridadeAcao, string> = {
  Baixa: 'Baixa',
  Media: 'Média',
  Alta: 'Alta',
  Critica: 'Crítica',
};

/**
 * Rotulo da complexidade de execucao.
 *
 * A ORDEM NUMERICA NO SERVIDOR E INVERTIDA da convencao usual (Alta = 1), por decisao do
 * time de Produto — a numeracao replica o que eles ja usam fora do sistema. O rotulo em
 * texto nao depende dessa ordem: o backend serializa como string.
 */
export const rotuloDaComplexidade: Record<ComplexidadeAcao, string> = {
  Alta: 'Alta',
  Media: 'Média',
  Baixa: 'Baixa',
};

/** Rotulo da area SESMT. */
export const rotuloDaAreaSesmt: Record<AreaSesmt, string> = {
  Saude: 'Saúde',
  Seguranca: 'Segurança',
};

/** Area SESMT, em ordem de exibicao no dropdown. */
export const areasSesmtEmOrdem: AreaSesmt[] = ['Saude', 'Seguranca'];

/**
 * Rotulo do modulo do sistema.
 *
 * MESMA ORDEM DO DROPDOWN pedida pelo Produto, e nao alfabetica: a lista reflete como o
 * time enxerga o sistema, nao como o dicionario ordenaria os nomes.
 */
export const rotuloDoModulo: Record<ModuloDoSistema, string> = {
  Agendas: 'Agendas',
  Ajuda: 'Ajuda',
  Ambulatorio: 'Ambulatório',
  Aso: 'ASO',
  Atestado: 'Atestado',
  Audiometria: 'Audiometria',
  BiometriaFacial: 'Biometria Facial',
  CargosESetores: 'Cargos e Setores',
  Cat: 'CAT',
  CentroDeCusto: 'Centro de Custo',
  Cipa: 'CIPA',
  Consulta: 'Consulta',
  Contestacao: 'Contestação',
  Documentos: 'Documentos',
  Empresa: 'Empresa',
  Enfermagem: 'Enfermagem',
  Epi: 'EPI',
  Ergonomia: 'Ergonomia',
  ESocial: 'eSocial',
  Exames: 'Exames',
  FapClinicas: 'FAP Clínicas',
  Funcionarios: 'Funcionários',
  Requisicao: 'Requisição',
  GestaoDeAfastados: 'Gestão de Afastados',
  BrigadaDeIncendio: 'Brigada de Incêndio',
  GestaoDePeriodicos: 'Gestão de Periódicos',
  Vacinas: 'Vacinas',
  Indicadores: 'Indicadores',
  Interface: 'Interface',
  Laudos: 'Laudos',
  LayoutUsabilidade: 'Layout/Usabilidade',
  Log: 'Log',
  MatrizDeRisco: 'Matriz de Risco',
  Medicamentos: 'Medicamentos',
  Medicos: 'Médicos',
  Ocorrencia: 'Ocorrência',
  Parametrizacoes: 'Parametrizações',
  Perfil: 'Perfil',
  Pericia: 'Perícia',
  PlanoDeAcao: 'Plano de Ação',
  PortalDoCandidato: 'Portal do Candidato',
  PortalDoFuncionario: 'Portal do Funcionário',
  ProntuarioDeEngenharia: 'Prontuário de Engenharia',
  ProntuarioMedico: 'Prontuário Médico',
  Readaptacao: 'Readaptação',
  RedeCredenciada: 'Rede Credenciada',
  Relatorios: 'Relatórios',
  Smartforms: 'Smartforms',
  Srq20: 'SRQ20',
  Subsidios: 'Subsídios',
  Terceiros: 'Terceiros',
  Treinamento: 'Treinamento',
  UnidadeDeTrabalho: 'Unidade de Trabalho (UT)',
  Upload: 'Upload',
  Usuarios: 'Usuários',
  WorkArea: 'Work Área',
};

/** Modulos, na mesma ordem pedida pelo Produto para o dropdown. */
export const modulosEmOrdem: ModuloDoSistema[] = [
  'Agendas',
  'Ajuda',
  'Ambulatorio',
  'Aso',
  'Atestado',
  'Audiometria',
  'BiometriaFacial',
  'CargosESetores',
  'Cat',
  'CentroDeCusto',
  'Cipa',
  'Consulta',
  'Contestacao',
  'Documentos',
  'Empresa',
  'Enfermagem',
  'Epi',
  'Ergonomia',
  'ESocial',
  'Exames',
  'FapClinicas',
  'Funcionarios',
  'Requisicao',
  'GestaoDeAfastados',
  'BrigadaDeIncendio',
  'GestaoDePeriodicos',
  'Vacinas',
  'Indicadores',
  'Interface',
  'Laudos',
  'LayoutUsabilidade',
  'Log',
  'MatrizDeRisco',
  'Medicamentos',
  'Medicos',
  'Ocorrencia',
  'Parametrizacoes',
  'Perfil',
  'Pericia',
  'PlanoDeAcao',
  'PortalDoCandidato',
  'PortalDoFuncionario',
  'ProntuarioDeEngenharia',
  'ProntuarioMedico',
  'Readaptacao',
  'RedeCredenciada',
  'Relatorios',
  'Smartforms',
  'Srq20',
  'Subsidios',
  'Terceiros',
  'Treinamento',
  'UnidadeDeTrabalho',
  'Upload',
  'Usuarios',
  'WorkArea',
];

/**
 * Rotulo da categoria de estado do work item.
 *
 * Tres palavras para o que o Azure chama de Proposed, InProgress e Completed. Em portugues e
 * no vocabulario de quem olha o painel, nao no do board.
 */
export const rotuloDaCategoriaDoWorkItem: Record<CategoriaDoWorkItem, string> = {
  Proposed: 'Não iniciado',
  InProgress: 'Em andamento',
  Completed: 'Concluído',
};

/**
 * Cor da categoria, reutilizando os tokens semanticos que ja existem.
 *
 * Nao ha paleta nova: "nao iniciado" usa o mesmo tom de espera das situacoes iniciais,
 * "em andamento" o de progresso e "concluido" o de sucesso. Cor nova aqui faria o mesmo
 * conceito ter dois tons na mesma tela.
 */
export const tokenDaCategoriaDoWorkItem: Record<CategoriaDoWorkItem, string> = {
  Proposed: '--semantico-neutro',
  InProgress: '--semantico-info',
  Completed: '--semantico-sucesso',
};

/** Texto do botao de cada transicao. */
export const rotuloDaTransicao: Record<TipoTransicao, string> = {
  Enviar: 'Enviar para análise',
  Assumir: 'Assumir análise',
  Aprovar: 'Aprovar',
  Rejeitar: 'Rejeitar',
  SolicitarAjustes: 'Solicitar ajustes',
  Reenviar: 'Reenviar',
  Priorizar: 'Colocar no backlog',
  Iniciar: 'Iniciar desenvolvimento',
  Concluir: 'Concluir',
  ReabrirRejeitada: 'Reabrir',
  ReabrirConcluida: 'Reabrir',
  Cancelar: 'Cancelar ação',
  VoltarParaBacklog: 'Voltar para o backlog',
  DevolverParaAnalise: 'Devolver para análise',
};

/** Caminho da rota de cada transicao, uma por transicao no servidor. */
export const rotaDaTransicao: Record<TipoTransicao, string> = {
  Enviar: 'enviar',
  Assumir: 'assumir',
  Aprovar: 'aprovar',
  Rejeitar: 'rejeitar',
  SolicitarAjustes: 'solicitar-ajustes',
  Reenviar: 'reenviar',
  Priorizar: 'priorizar',
  Iniciar: 'iniciar',
  Concluir: 'concluir',
  ReabrirRejeitada: 'reabrir-rejeitada',
  ReabrirConcluida: 'reabrir-concluida',
  Cancelar: 'cancelar',
  VoltarParaBacklog: 'voltar-para-backlog',
  DevolverParaAnalise: 'devolver-para-analise',
};

/**
 * Transicoes que exigem justificativa.
 *
 * ISTO E UMA COPIA de MaquinaEstados.ExigeJustificativa, e a duplicacao e
 * consciente. Serve para pedir o motivo ANTES de enviar; a regra continua sendo
 * do servidor, que responde 409 se o motivo faltar.
 *
 * O risco da copia e ficar defasada. O sintoma seria brando nos dois sentidos:
 * uma transicao que passou a exigir motivo responderia 409 com a mensagem do
 * dominio, e uma que deixou de exigir apenas pediria um texto a mais. Nenhum dos
 * dois permite gravar algo que o dominio recusaria.
 */
export const transicoesQueExigemJustificativa: ReadonlySet<TipoTransicao> = new Set([
  'Rejeitar',
  'SolicitarAjustes',
  'ReabrirRejeitada',
  'ReabrirConcluida',
  'Cancelar',
  'VoltarParaBacklog',
  'DevolverParaAnalise',
]);

/** Rotulo do campo de justificativa, por transicao. */
export function rotuloDaJustificativa(transicao: TipoTransicao): string {
  return transicao === 'Cancelar' ? 'Motivo do cancelamento' : 'Justificativa';
}
